// A page whose shell and whose chunks came from two builds (#2138).
//
// During a rollout both zenith-api pods serve for ADR-0438's hold, and each
// image holds only its own build's hashed chunks. A shell from one pod names
// chunks by the hashes it was built with, and a chunk whose content changed is
// a 404 on the other (ADR-0003: a missing asset is never the shell). Measured
// on staging 2026-10-29: the shell and `index-D2U-8cg0.js` from the old pod,
// `DeviceDetail-C4zmf7EA.js` answered 404 by the new one.
//
// The load balancer's affinity (deploy/*/ingress.yaml, the zenith-api
// BackendConfig) keeps a browser on one pod while both are attached. This is
// the half it cannot cover: a pod detached between the shell and its chunks.
//
// `staleBundle.ts` (#457) handles a lazy chunk, and cannot handle this one. A
// chunk the entry imports statically is fetched before the entry runs, and the
// entry is what installs that listener, so when one fails nothing of the
// application ever runs: the page stays blank. So this runs first, as an
// inline script in index.html's head, ahead of the module script Vite injects.
// The entry stands it down the moment it runs (`stopEntryGuard`), because from
// then on a failed chunk is a lazy one and `staleBundle.ts`'s.
//
// **It is inlined from this file, not written twice.** `entryGuardScript`
// serialises `installEntryGuard` and vite.config.ts writes the result into the
// built index.html, so the function cannot reach anything outside its own body:
// every value it needs arrives in its options, and the test runs the serialised
// form.

import { STALE_BUNDLE_KEY } from "./staleBundle";

/** The window property the entry calls to stand the guard down. */
export const ENTRY_GUARD_STOP = "__zenithStopEntryGuard";

/** What the page says when it cannot draw and will not reload again. Neutral on
 * purpose: the shell has no brand yet, and a reseller's page must not name
 * Zenith. */
export const ENTRY_GUARD_MESSAGE =
  "This page was updated while it was loading and could not be drawn. Reload it to open the current version.";

export interface EntryGuardOptions {
  /** The build this shell was compiled from: the same identity `staleBundle.ts`
   * keys on, so a reload either one made counts for both. */
  build: string;
  /** Where the attempt is remembered: `STALE_BUNDLE_KEY`. */
  key: string;
  /** The window property the entry calls to stand the guard down. */
  stopName: string;
  /** Drawn into an empty `#root` when the page will not reload. */
  message: string;
  /** Injected so a test does not navigate jsdom. */
  win?: Window;
  reload?: () => void;
}

/**
 * Reload once when the entry module, or a module it names, does not load.
 *
 * **Self-contained**: it is serialised into index.html, so it reads nothing
 * from this module's scope.
 *
 * The rule is #457's. Keyed to the build, so a build that genuinely lacks a
 * chunk its own entry names reloads once and then says so rather than looping;
 * and where `sessionStorage` cannot hold the attempt, nothing reloads, because
 * an unguarded reload is that loop.
 */
export function installEntryGuard(options: EntryGuardOptions): void {
  const win = options.win ?? window;
  const reload = options.reload ?? (() => win.location.reload());
  const say = () => {
    const root = win.document.getElementById("root");
    if (root && !root.hasChildNodes()) root.textContent = options.message;
  };
  const onError = (event: Event) => {
    // A module script or a modulepreload link that did not load. An image, a
    // stylesheet or a script that threw is not this guard's: a load error does
    // not bubble, so only this capture listener sees one, and a thrown error's
    // target is the window, not an element.
    const el = event.target as Element | null;
    const tag = el && el.tagName;
    const isModule =
      (tag === "SCRIPT" && el!.getAttribute("type") === "module") ||
      (tag === "LINK" && el!.getAttribute("rel") === "modulepreload");
    if (!isModule) return;
    stop();
    let attempted: string | null;
    try {
      attempted = win.sessionStorage.getItem(options.key);
    } catch {
      say(); // No guard available: say so rather than risk a loop.
      return;
    }
    // This build already reloaded and the page still cannot load.
    if (attempted === options.build) {
      say();
      return;
    }
    try {
      win.sessionStorage.setItem(options.key, options.build);
    } catch {
      say();
      return;
    }
    reload();
  };
  const stop = () => win.removeEventListener("error", onError, true);
  win.addEventListener("error", onError, true);
  (win as unknown as Record<string, unknown>)[options.stopName] = stop;
}

/** The entry runs: every module it imports statically has loaded, so a chunk
 * that fails from here on is a lazy one, and `staleBundle.ts` handles it. */
export function stopEntryGuard(win: Window = window): void {
  const stop = (win as unknown as Record<string, unknown>)[ENTRY_GUARD_STOP];
  if (typeof stop === "function") stop();
}

/** The inline script vite.config.ts writes into the built index.html. `<` is
 * escaped so nothing in the options can close the script element. */
export function entryGuardScript(build: string): string {
  const options = { build, key: STALE_BUNDLE_KEY, stopName: ENTRY_GUARD_STOP, message: ENTRY_GUARD_MESSAGE };
  return `(${installEntryGuard.toString()})(${JSON.stringify(options).replace(/</g, "\\u003c")});`;
}
